import {CloseOutlined} from '@ant-design/icons';
import {UniqueIdentifier, useDraggable, useDroppable} from '@dnd-kit/core';
import _ from 'lodash';
import React, {useContext} from "react";
import {selectFields} from '../../containers/QueryBuilder/queryBuilderSlice';
import {QueryField} from '../../containers/QueryBuilder/types/Query';
import {UserQuery} from '../../containers/QueryBuilder/types/UserQuery';
import {useAppSelector} from "../../hooks";
import {FILTER, isCohortField, VALUE} from './QueryBuilder';
import {QueryBuilderContext, QueryBuilderContextType} from './QueryBuilderContext';
import styles from './QueryInputList.module.scss';
import {message} from "antd";

export const removeItem = (id: string, userQuery: UserQuery, qf: QueryField, text?: string) => {

    if (id === FILTER && (userQuery.query.filterLabels && userQuery.query.filterLabels.length > 0)) {
        _.remove(userQuery.query.filterLabels, { field: qf.code })
    }

    // Handle removing from QueryField lists dynamically
    type ObjectKey = keyof typeof userQuery.query;
    const key = `${id}s` as ObjectKey;
    const list = userQuery.query[key] as QueryField[];

    if (Array.isArray(list)) {
        // Match only `code` and optionally `aggtype` if present
        _.remove(list, (field: QueryField) =>
            field.code === qf.code &&
            (qf.aggtype === undefined || field.aggtype === qf.aggtype)
        );
        // @ts-ignore
        userQuery.query[key] = list; // Update the list after modification
    }
}

export const itemText = (id: string, name: string, qf: QueryField): string => {
    let text = name
    switch (id) {
        case VALUE:
            if (qf.aggtype) {
                text += ` (${qf.aggtype})`
            }
            break
        case FILTER:
            if (qf.value) {
                text += ` (${qf.value.split(',').length})`
            }
            break
    }
    return text
}

const Item = ({ id, qf }: { id: UniqueIdentifier, qf: QueryField }) => {

    const {
        userQuery,
        setUserQuery,
        setCurrentField,
        setIsValueModalOpen,
        setIsFilterModalOpen
    } = useContext(QueryBuilderContext) as QueryBuilderContextType

    const fields = useAppSelector(selectFields)!

    const onClick = (e: React.MouseEvent<HTMLElement>) => {
        switch (id) {
            case VALUE:
                if (!(field.simpleAggregation)) {
                    setCurrentField({ field: fields[qf.code], queryField: qf })
                    setIsValueModalOpen(true)
                } else {
                    message.warn("This field does not have any aggregation choices.");
                }
                break
            case FILTER:
                setCurrentField({ field: fields[qf.code], queryField: qf })
                setIsFilterModalOpen(true)
                break
        }
    }

    const clazzName = id === VALUE || id === FILTER ? styles.item_clickable : styles.item
    const field = fields[qf.code]

    const { attributes, listeners, setNodeRef } = useDraggable({
        id: `draggable-${id}-${field ? field.code : ''}`,
        data: { from: id, field: field },
    })

    if (field) {
        const isCohortAnalyserField = isCohortField(field.code)
        let text = itemText(id as string, field.name, qf)
        const remove = (e: React.MouseEvent<HTMLElement>) => {
            e.stopPropagation()
            removeItem(id as string, userQuery, qf, text)
            setUserQuery({ ...userQuery })
        }

        return (
            <div className={clazzName} onClick={(e) => {
                onClick(e)
            }}>
                <span ref={setNodeRef} {...attributes} {...listeners}>{text}</span>
                <CloseOutlined
                    data-testid={`remove-${id}-${field.code}`}
                    onClick={(e) => remove(e)}
                    hidden={isCohortAnalyserField}
                />
            </div>
        )
    }
    // else fields associated with a parent filter
    return null
}

type QueryInputListProps = {
    id: UniqueIdentifier
    values: QueryField[]
    title: React.ReactNode
    enabled: boolean
    additionalClazz?: String
    children?: React.ReactNode
    dataTestId?: String
}

export const QueryInputList = ({ id, values, title, enabled, additionalClazz, children, dataTestId}: QueryInputListProps) => {

    const { active, setNodeRef } = useDroppable({
        id: id,
        data: { enabled: enabled }
    })

    const className = active ? (enabled ? styles.droppable_yes : styles.droppable_no) : styles.droppable
    return <div data-testid={dataTestId} ref={setNodeRef} className={`${styles.container} ${className} ${additionalClazz?additionalClazz:''}`}>
        <div className={styles.header}>{title}</div>
        <div className={styles.list}>
            {values.map(i => <Item key={`${id}-${i.code}-${i.aggtype}`} id={id} qf={i} />)}
        </div>
        {children}
    </div>
}

